// ──────────────────────────────────────────────────────────
// reel.model.ts
// Unified model + display extraction for all 7 resource types:
// Pet, Car, Sale2, Required, Teacher, Nadlan, Coworker
// Now includes: avg_rating, ratings_count, ratings
// ──────────────────────────────────────────────────────────

export interface ReelItem {
    id: number;
    name?: string;
    description?: string;
    price?: number | null;
    images?: ReelImage[];
    video_url?: string | null;
    video_key?: string | null;
    resourcetype?: string;
    mainCategory?: MainCategory;
    sconderyCategory?: any;
    city?: CityField;
    region?: LabelField;
    createTime?: string;
    user_loves_it?: boolean;
    user_rating?: number;
    // Reel-specific fields (from backend ReelFieldsMixin)
    avg_rating?: number | null;
    ratings_count?: number;
    ratings?: ReelRating[];

    [key: string]: any;
}

export interface ReelRating {
    id: number;
    rating: number;
    comments: string;
    date_created: string;
    date_created_gmt: string;
}

export interface ReelImage {
    image: string;
    SortNum: number | null;
}

export interface LabelField {
    id: number;
    label: string;
    icon: string;
    value: { value: number; name: string };
}

export interface CityField {
    id: number;
    nameHe: string;
    nameEng: string;
    cityCode: number;
    areaName: string;
    [key: string]: any;
}

export interface MainCategory {
    id: number;
    name: string;
    icon: string;
    category: string;
    [key: string]: any;
}

// ─── Display output ───

export interface ReelDisplayData {
    title: string;
    subtitle: string;
    description: string;
    tags: ReelTag[];
    priceLine: string;
    priceIsFree: boolean;
    accentColor: string;
    typeIcon: string;
    typeLabel: string;
    location: string;
    avgRating: number | null;
    ratingsCount: number;
}

export interface ReelTag {
    label: string;
    icon?: string;
}

// ─── Internal type (without rating — added by wrapper) ───

type BaseDisplay = Omit<ReelDisplayData, 'avgRating' | 'ratingsCount'>;

// ─── Main extraction function ───

export function extractReelDisplay(reel: ReelItem): ReelDisplayData {
    let base: BaseDisplay;
    switch ((reel.resourcetype || '').toLowerCase()) {
        case 'pet':       base = pet(reel); break;
        case 'car':       base = car(reel); break;
        case 'sale2':     base = sale2(reel); break;
        case 'required':  base = required(reel); break;
        case 'teacher':   base = teacher(reel); break;
        case 'nadlan':    base = nadlan(reel); break;
        case 'coworker':  base = coworker(reel); break;
        default:          base = generic(reel); break;
    }
    return {
        ...base,
        avgRating: reel.avg_rating ?? null,
        ratingsCount: reel.ratings_count ?? 0,
    };
}

// ─── Pet ───
function pet(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.age) tags.push({ label: r.age.label, icon: r.age.icon });
    if (r.gender) tags.push({ label: r.gender.label, icon: r.gender.icon });
    if (r.size) tags.push({ label: r.size.label });
    if (r.color && typeof r.color === 'string') tags.push({ label: r.color });
    return {
        title: r.petName || r.name || '',
        subtitle: r.breed?.label || r.petType?.label || '',
        description: r.description || '',
        tags,
        priceLine: r.freeAdoption ? 'לאימוץ חינם' : r.price ? `₪${r.price}` : '',
        priceIsFree: !!r.freeAdoption,
        accentColor: '#6c5ce7',
        typeIcon: 'pets',
        typeLabel: r.listingType?.label || 'חיות מחמד',
        location: r.city?.nameHe?.trim() || '',
    };
}

// ─── Car ───
function car(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.year) tags.push({ label: `${r.year}`, icon: 'calendar_today' });
    if (r.mileage) tags.push({ label: `${r.mileage.toLocaleString()} ק"מ`, icon: 'speed' });
    if (r.engineCapacity) tags.push({ label: `${r.engineCapacity} סמ"ק`, icon: 'engineering' });
    if (r.gearBox) tags.push({ label: r.gearBox === '1' ? 'אוטומט' : 'ידני', icon: 'settings' });
    if (r.Color?.colorName) tags.push({ label: r.Color.colorName });
    if (r.NumberOfOwner) tags.push({ label: r.NumberOfOwner.numberOnwer });
    return {
        title: r.name || `${r.CodeProducer || ''} ${r.MainModel || ''}`.trim(),
        subtitle: r.CodeProducer || '',
        description: r.description || '',
        tags,
        priceLine: r.price ? `₪${r.price.toLocaleString()}` : r.exchange ? 'להחלפה' : '',
        priceIsFree: false,
        accentColor: '#00b894',
        typeIcon: 'directions_car',
        typeLabel: 'רכב',
        location: r.city?.nameHe?.trim() || '',
    };
}

// ─── Sale2 (Second-hand) ───
function sale2(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.ProductStatus) tags.push({ label: r.ProductStatus.statusName, icon: 'new_releases' });
    if (r.subCategory) tags.push({ label: r.subCategory.subCategoryName });
    if (r.DeliveryOffer) tags.push({ label: 'משלוח', icon: 'local_shipping' });
    return {
        title: r.name || '',
        subtitle: r.sconderyCategory?.name || '',
        description: r.description || r.FreeText || '',
        tags,
        priceLine: r.price ? `₪${r.price}` : '',
        priceIsFree: false,
        accentColor: '#e17055',
        typeIcon: 'shopping_cart',
        typeLabel: 'יד שניה',
        location: (r.City?.nameHe || r.city?.nameHe || '').trim(),
    };
}

// ─── Required (Jobs) ───
function required(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.jobCategory) tags.push({ label: r.jobCategory.label, icon: r.jobCategory.icon });
    if (r.jobType) tags.push({ label: r.jobType.label, icon: r.jobType.icon });
    if (r.workLocationType) tags.push({ label: r.workLocationType.label, icon: r.workLocationType.icon });
    if (r.isNegotiable) tags.push({ label: 'גמיש', icon: 'handshake' });
    let priceLine = '';
    if (r.salaryFrom && r.salaryTo) {
        priceLine = `₪${r.salaryFrom}-₪${r.salaryTo}`;
        if (r.salaryType?.label) priceLine += ` ${r.salaryType.label}`;
    }
    return {
        title: r.jobTitle || r.name || '',
        subtitle: r.isAnonymous ? '' : r.companyName || '',
        description: r.description || '',
        tags,
        priceLine,
        priceIsFree: false,
        accentColor: '#0984e3',
        typeIcon: 'work',
        typeLabel: 'דרושים',
        location: r.city?.nameHe?.trim() || '',
    };
}

// ─── Teacher ───
function teacher(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.teachingSubjects?.length) {
        r.teachingSubjects.forEach((s: LabelField) => tags.push({ label: s.label, icon: s.icon }));
    }
    if (r.teacherEducation) tags.push({ label: r.teacherEducation.label, icon: r.teacherEducation.icon });
    if (r.experienceYears) tags.push({ label: r.experienceYears.label, icon: r.experienceYears.icon });
    if (r.freeTrialLesson) tags.push({ label: 'שיעור ניסיון חינם', icon: 'card_giftcard' });
    if (r.groupLesson) tags.push({ label: 'שיעור קבוצתי', icon: 'groups' });
    let priceLine = '';
    if (r.pricePerHour) priceLine = `₪${r.pricePerHour}/שעה`;
    if (r.pricePerPackage && r.packageSize) {
        const pkg = `חבילה: ₪${r.pricePerPackage}/${r.packageSize} שיעורים`;
        priceLine = priceLine ? `${priceLine} | ${pkg}` : pkg;
    }
    return {
        title: r.teacherTitle || r.name || '',
        subtitle: r.isAnonymous ? '' : r.businessName || '',
        description: r.description || '',
        tags,
        priceLine,
        priceIsFree: !!r.freeTrialLesson,
        accentColor: '#fdcb6e',
        typeIcon: 'school',
        typeLabel: 'מורים פרטיים',
        location: r.city?.nameHe?.trim() || '',
    };
}

// ─── Nadlan (Real Estate) ───
function nadlan(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.TypeOfNadlan?.nameHe) tags.push({ label: r.TypeOfNadlan.nameHe.trim(), icon: 'apartment' });
    if (r.RoomsNum) tags.push({ label: `${r.RoomsNum.numberOfRooms} חדרים`, icon: 'meeting_room' });
    if (r.Floor != null) tags.push({ label: `קומה ${r.Floor}/${r.TotalFloor || '?'}`, icon: 'stairs' });
    if (r.buildingMeter) tags.push({ label: `${r.buildingMeter} מ"ר`, icon: 'square_foot' });
    if (r.ParkingNumber) tags.push({ label: `${r.ParkingNumber.numberParking} חניות`, icon: 'local_parking' });
    if (r.OnColumns) tags.push({ label: 'על עמודים', icon: 'foundation' });

    const loc = (r.City?.nameHe || r.city?.nameHe || '').trim();
    const street = r.Street?.nameHe || '';
    const fullLoc = street ? `${loc} - ${street}` : loc;
    const scName = typeof r.sconderyCategory === 'object' ? r.sconderyCategory?.name : '';

    return {
        title: r.name || '',
        subtitle: scName || '',
        description: r.description || '',
        tags,
        priceLine: r.price ? `₪${r.price.toLocaleString()}` : '',
        priceIsFree: false,
        accentColor: '#d63031',
        typeIcon: 'home',
        typeLabel: scName || 'נדל"ן',
        location: fullLoc,
    };
}

// ─── Coworker (Professionals) ───
function coworker(r: ReelItem): BaseDisplay {
    const tags: ReelTag[] = [];
    if (r.professionCategory) tags.push({ label: r.professionCategory.label, icon: r.professionCategory.icon });
    if (r.serviceType) tags.push({ label: r.serviceType.label, icon: r.serviceType.icon });
    if (r.availability) tags.push({ label: r.availability.label, icon: r.availability.icon });
    if (r.experienceYears) tags.push({ label: r.experienceYears.label, icon: r.experienceYears.icon });
    if (r.freeEstimate) tags.push({ label: 'הערכה חינם', icon: 'card_giftcard' });

    let priceLine = '';
    if (r.priceFrom && r.priceTo) {
        priceLine = `₪${r.priceFrom}-₪${r.priceTo}`;
        if (r.pricingType?.label) priceLine += ` ${r.pricingType.label}`;
    }

    return {
        title: r.professionalTitle || r.name || '',
        subtitle: r.isAnonymous ? '' : r.businessName || '',
        description: r.description || '',
        tags,
        priceLine,
        priceIsFree: !!r.freeEstimate,
        accentColor: '#00cec9',
        typeIcon: 'construction',
        typeLabel: 'בעלי מקצוע',
        location: r.city?.nameHe?.trim() || '',
    };
}

// ─── Generic fallback ───
function generic(r: ReelItem): BaseDisplay {
    return {
        title: r.name || '',
        subtitle: '',
        description: r.description || '',
        tags: [],
        priceLine: r.price ? `₪${r.price}` : '',
        priceIsFree: false,
        accentColor: '#636e72',
        typeIcon: 'storefront',
        typeLabel: r.mainCategory?.name || '',
        location: (r.city?.nameHe || '').trim(),
    };
}